import type { ApiResult } from './types';
import { apiRequest, commonHeaders } from './apiClient';
import { buildProfilePicPayloadFields, extractProfilePictureFields } from '../utils/profilePictureRef';
import { formatAuditDateTime } from '../utils/dateFormat';

export type AccountStatus = 'Active' | 'Inactive';

export type IntranetUserRole =
  | 'System Administrator'
  | 'State Counsel'
  | 'Allocating Officer'
  | 'Supervising Officer'
  | 'Division Staff'
  | 'Assigned Officer'
  | 'Second Assigned Officer'
  | 'Mail Room / Registry Officer'
  | 'Record Room Officer';

export type IntranetDivision = string;

export type DivisionListItem = { id: number; name: string };

export type DesignationListItem = { id: number; name: string; code: string; order_index: number };

export type IntranetUserTitle = 'Mr.' | 'Ms.' | 'Mrs.';

/** One assigned role; exactly one should have defaultRole true when saving. */
export type IntranetUserRoleEntry = {
  roleId: number;
  roleName: string;
  defaultRole: boolean;
};

export type IntranetUser = {
  id: string;
  title: IntranetUserTitle | '';
  firstName: string;
  lastName: string;
  displayName: string;
  email: string; // username
  nicOrPassport: string;
  /** Default / primary role name (legacy + filters); keep in sync with userRoles default. */
  role: IntranetUserRole | '';
  roleId?: number;
  /** Multiple roles from API (create/update uses roles[]). */
  userRoles: IntranetUserRoleEntry[];
  /** List API may return a single formatted string for the table column. */
  rolesDisplay?: string;
  division: IntranetDivision | '';
  divisionId?: number;
  designationId?: number;
  designation?: string;
  serviceId?: string;
  contactNumber?: string;
  status: AccountStatus;
  /** Full URL for profile image when backend provides it directly. */
  profilePictureIconFullpath?: string;
  /** DMS storage key (UUID) from view-user-by-id — fetch via file-management download. */
  profilePictureIconUrl?: string;
  /**
   * DMS file reference returned by `/v1/file-management/upload`.
   * Keep this in-memory (React state) and only send to API on save.
   */
  profilePicFileRef?: string;
  profilePicFileChanged?: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type ExtranetUser = {
  id: string;
  agencyId?: number;
  agencyName: string;
  companyCode?: string;
  brn?: string;
  displayName: string;
  email: string; // username
  contactPersonName: string;
  contactPersonDesignation?: string;
  contactNumber?: string;
  status: AccountStatus;
  /** Full URL for profile image when backend provides it directly. */
  profilePictureIconFullpath?: string;
  /** DMS storage key (UUID) from view-user-by-id — fetch via file-management download. */
  profilePictureIconUrl?: string;
  /**
   * DMS file reference returned by `/v1/file-management/upload`.
   * Keep this in-memory (React state) and only send to API on save.
   */
  profilePicFileRef?: string;
  profilePicFileChanged?: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

type CreateIntranetUserRequest = {
  data: {
    title: string;
    first_name: string;
    last_name: string;
    display_name: string;
    username: string;
    nic: string;
    roles: Array<{ role_id: number; default_role: boolean }>;
    division_id: number;
    // designation: string;
    designation_id: number | null;
    service_id: string | null;
    contact_number: string | null;
    profile_pic_file_ref: string | null;
    profile_pic_file_changed: boolean;
    active: boolean;
  };
};

type CreateExtranetUserRequest = {
  data: {
    agency_id: number;
    company_code: string | null;
    brn: string | null;
    display_name: string;
    username: string;
    contact_person_name: string;
    contact_number: string | null;
    contact_person_designation: string | null;
    profile_pic_file_ref: string | null;
    profile_pic_file_changed: boolean;
    active: boolean;
  };
};

function withAppCode(applicationCode: string): { applicationCode: string } {
  // Always include shared headers, but allow endpoint-specific app code.
  return { ...commonHeaders, applicationCode };
}

export type ActiveAppUser = {
  id: string;
  username: string;
  displayName: string;
  active: boolean;
  profilePictureIconUrl: string;
  email: string;
  defaultRoleId: number;
  defaultRole: string;
  roleDescription: string;
};

type ActiveAppUserResponse = {
  user: {
    id: string;
    username?: string | null;
    display_name?: string | null;
    active?: boolean | null;
    profile_picture_icon_url?: string | null;
    email?: string | null;
    default_role_id?: number | null;
    default_role?: string | null;
    role_description?: string | null;
  };
};

export async function getActiveUserAndPermission(): Promise<ApiResult<ActiveAppUser>> {
  const res = await apiRequest<ActiveAppUserResponse>(`/v1/user/app-user/active`, {
    method: 'GET',
    headers: withAppCode(import.meta.env.VITE_APP_CODE_ACTIVE_USER ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };

  const u = res.data?.user;
  if (!u?.id) return { success: false, message: 'Invalid active user response.' };

  return {
    success: true,
    data: {
      id: u.id,
      username: (u.username ?? '').trim(),
      displayName: (u.display_name ?? '').trim(),
      active: Boolean(u.active),
      profilePictureIconUrl: (u.profile_picture_icon_url ?? '').trim(),
      email: (u.email ?? '').trim(),
      defaultRoleId: typeof u.default_role_id === 'number' ? u.default_role_id : 0,
      defaultRole: (u.default_role ?? '').trim(),
      roleDescription: (u.role_description ?? '').trim(),
    },
  };
}

type RolePermissionCodeRow = { permission_code?: string | null } | { permissionCode?: string | null };

/**
 * CU-AUC-46 — GET `/v1/user/app-user/role-permissons` (note: backend path typo is "permissons").
 * Returns permission codes for the given role.
 */
export async function getRolePermissionsByRoleId(input: {
  roleId: number;
  applicationCode?: string;
}): Promise<ApiResult<string[]>> {
  const roleId = input.roleId;
  if (!Number.isFinite(roleId) || roleId <= 0) return { success: false, message: 'Invalid role id.' };

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_ROLE_PERMISSIONS ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const qs = new URLSearchParams({ roleId: String(roleId) });
  const res = await apiRequest<RolePermissionCodeRow[]>(`/v1/user/app-user/role-permissons?${qs.toString()}`, {
    method: 'GET',
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };

  const codes = (res.data ?? [])
    .map((r) => {
      const anyRow = r as Record<string, unknown>;
      const raw = (anyRow.permission_code ?? anyRow.permissionCode) as unknown;
      return typeof raw === 'string' ? raw.trim() : '';
    })
    .filter(Boolean);

  return { success: true, data: Array.from(new Set(codes)) };
}

export async function updateAppUserLoginDetailsOnLogin(input: {
  id: string;
  currentLoginIp: string;
  newSession: boolean;
}): Promise<
  ApiResult<{
    id: string;
    category_id?: number;
    username?: string;
    display_name?: string;
    active?: boolean;
    invalid_login_count?: number;
    last_login_at?: string;
    current_login_at?: string;
    current_login_ip?: string;
    last_login_ip?: string;
    verification_email?: string;
    meta_user?: boolean;
    created_at?: string;
    updated_at?: string;
    migrated?: boolean;
    updated_by?: string;
  }>
> {
  const id = input.id.trim();
  if (!id) return { success: false, message: 'Missing user id.' };

  const res = await apiRequest<{ data?: Record<string, unknown> }>(`/v1/user/app-user/login/id/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: {
      data: {
        current_login_ip: input.currentLoginIp,
        new_session: input.newSession,
      },
    },
    headers: withAppCode(import.meta.env.VITE_APP_CODE_LOGIN_UPDATE ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };

  const d = (res.data?.data ?? {}) as Record<string, unknown>;
  return {
    success: true,
    data: {
      id: typeof d.id === 'string' ? d.id : id,
      category_id: typeof d.category_id === 'number' ? d.category_id : undefined,
      username: typeof d.username === 'string' ? d.username : undefined,
      display_name: typeof d.display_name === 'string' ? d.display_name : undefined,
      active: typeof d.active === 'boolean' ? d.active : undefined,
      invalid_login_count: typeof d.invalid_login_count === 'number' ? d.invalid_login_count : undefined,
      last_login_at: typeof d.last_login_at === 'string' ? d.last_login_at : undefined,
      current_login_at: typeof d.current_login_at === 'string' ? d.current_login_at : undefined,
      current_login_ip: typeof d.current_login_ip === 'string' ? d.current_login_ip : undefined,
      last_login_ip: typeof d.last_login_ip === 'string' ? d.last_login_ip : undefined,
      verification_email: typeof d.verification_email === 'string' ? d.verification_email : undefined,
      meta_user: typeof d.meta_user === 'boolean' ? d.meta_user : undefined,
      created_at: typeof d.created_at === 'string' ? d.created_at : undefined,
      updated_at: typeof d.updated_at === 'string' ? d.updated_at : undefined,
      migrated: typeof d.migrated === 'boolean' ? d.migrated : undefined,
      updated_by: typeof d.updated_by === 'string' ? d.updated_by : undefined,
    },
  };
}

type InternalUsersListRequest = {
  data: {
    name?: string;
    email?: string;
    role?: string;
    division?: string;
    active?: boolean;
  };
};

type InternalUsersListResponse = {
  content: Array<{
    id: string;
    name: string;
    role: string;
    division: string;
    email: string;
    createdAt?: string;
    created_at?: string;
    updatedAt?: string;
    updated_at?: string;
    active: boolean;
  }>;
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
};

type ExternalUsersListRequest = {
  data: {
    agency_name?: string;
    email?: string;
    active?: boolean;
  };
};

type ExternalUsersListResponse = {
  totalPages?: number;
  totalElements?: number;
  numberOfElements?: number;
  size?: number;
  number?: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
  content: Array<{
    user_id: string;
    agency_name: string;
    code: string;
    email: string;
    contact_person: string;
    date_added: string;
    active: boolean;
    role?: string;
    last_updated_at?: string;
  
  }>;
};

type ViewUserByIdResponse = {
  app_user_id: string;
  username: string;
  user_display_name: string;
  title?: string;
  first_name?: string;
  last_name?: string;
  nic?: string;
  role_id?: number;
  role_name?: string;
  user_roles?: Array<{ role_id: number; role_name?: string; default_role?: boolean }>;
  division_id?: number;
  division_name?: string;
  designation?: string;
  designation_id?: number;
  designationId?: number;
  service_id?: string;
  contact_number?: string;
  active?: boolean;
  created_at?: string;
  created_by_name?: string;
  updated_at?: string;
  updated_by_name?: string;
  profile_picture_icon_url?: string | null;
  profilePictureIconUrl?: string | null;
  profile_picture_icon_url_fullpath?: string | null;
  profilePictureIconUrlFullpath?: string | null;
  profile_pic_file_ref?: string | null;
  profilePicFileRef?: string | null;
  agency_name?: string;
  company_code?: string;
  brn?: string;
  contact_person_name?: string;
  contact_person_designation?: string;
};

export type IntranetUserListPage = {
  items: IntranetUser[];
  totalElements: number;
  totalPages: number;
  /** Zero-based page index as returned by Spring (same as request `pageNumber` query). */
  pageNumber: number;
  pageSize: number;
};

export function formatIntranetAuditDateTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const formatted = formatAuditDateTime(iso);
  return formatted || '—';
}

function listItemUpdatedAt(u: InternalUsersListResponse['content'][number]): string | undefined {
  return u.updatedAt ?? u.updated_at ?? u.createdAt ?? u.created_at;
}

function buildRolesPayload(user: IntranetUser): Array<{ role_id: number; default_role: boolean }> {
  const fromMulti =
    user.userRoles?.filter((r) => r.roleId > 0).map((r) => ({ role_id: r.roleId, default_role: Boolean(r.defaultRole) })) ?? [];
  if (fromMulti.length) return fromMulti;
  const rid = user.roleId ?? 0;
  if (rid > 0) return [{ role_id: rid, default_role: true }];
  return [];
}

function intranetToCreatePayload(user: IntranetUser): CreateIntranetUserRequest {
  const { profile_pic_file_changed, profile_pic_file_ref } = buildProfilePicPayloadFields({
    profilePicFileChanged: user.profilePicFileChanged,
    profilePicFileRef: user.profilePicFileRef,
    existingProfilePicRef: user.profilePictureIconUrl,
  });
  const serviceId = user.serviceId?.trim() ?? '';
  const contactNumber = user.contactNumber?.trim() ?? '';
  const roles = buildRolesPayload(user);
  return {
    data: {
      title: user.title,
      first_name: user.firstName.trim(),
      last_name: user.lastName.trim(),
      display_name: `${user.title} ${user.firstName} ${user.lastName}`.replace(/\s+/g, ' ').trim() || user.displayName.trim(),
      username: user.email.trim(),
      nic: user.nicOrPassport.trim(),
      roles,
      division_id: user.divisionId ?? 0,
      designation_id: user.designationId ?? null,
      service_id: serviceId ? serviceId : null,
      contact_number: contactNumber ? contactNumber : null,
      profile_pic_file_changed,
      profile_pic_file_ref,
      active: user.status === 'Active',
    },
  };
}

function extranetToCreatePayload(user: ExtranetUser): CreateExtranetUserRequest {
  const { profile_pic_file_changed, profile_pic_file_ref } = buildProfilePicPayloadFields({
    profilePicFileChanged: user.profilePicFileChanged,
    profilePicFileRef: user.profilePicFileRef,
    existingProfilePicRef: user.profilePictureIconUrl,
  });
  const contactNumber = user.contactNumber?.trim() ?? '';
  const companyCode = user.companyCode?.trim() ?? '';
  const brn = user.brn?.trim() ?? '';
  const designation = user.contactPersonDesignation?.trim() ?? '';
  return {
    data: {
      agency_id: user.agencyId ?? 0,
      company_code: companyCode ? companyCode : null,
      brn: brn ? brn : null,
      display_name: user.displayName,
      username: user.email,
      contact_person_name: user.contactPersonName,
      contact_number: contactNumber ? contactNumber : null,
      contact_person_designation: designation ? designation : null,
      profile_pic_file_changed,
      profile_pic_file_ref,
      active: user.status === 'Active',
    },
  };
}

function mapViewInternalToIntranetUser(r: ViewUserByIdResponse): IntranetUser {
  const title = (r.title ?? '').trim();
  const safeTitle: IntranetUser['title'] =
    title === 'Mr.' || title === 'Ms.' || title === 'Mrs.' ? title : '';
  const rawRoles = Array.isArray(r.user_roles) ? r.user_roles : [];
  const userRoles: IntranetUserRoleEntry[] = rawRoles
    .map((x) => ({
      roleId: typeof x.role_id === 'number' ? x.role_id : 0,
      roleName: (x.role_name ?? '').trim(),
      defaultRole: Boolean(x.default_role),
    }))
    .filter((x) => x.roleId > 0 && x.roleName.length > 0);

  if (!userRoles.length && typeof r.role_id === 'number' && r.role_id > 0) {
    userRoles.push({
      roleId: r.role_id,
      roleName: (r.role_name ?? '').trim() || 'Assigned Officer',
      defaultRole: true,
    });
  }
  if (!userRoles.length && (r.role_name ?? '').trim()) {
    userRoles.push({
      roleId: typeof r.role_id === 'number' ? r.role_id : 0,
      roleName: (r.role_name ?? '').trim(),
      defaultRole: true,
    });
  }

  const normalizedUserRoles =
    userRoles.length && !userRoles.some((x) => x.defaultRole)
      ? userRoles.map((x, i) => ({ ...x, defaultRole: i === 0 }))
      : userRoles;

  const defaultEntry = normalizedUserRoles.find((x) => x.defaultRole) ?? normalizedUserRoles[0];
  const roleName = (defaultEntry?.roleName ?? r.role_name ?? 'Assigned Officer') as IntranetUser['role'];
  const roleId = defaultEntry?.roleId ?? r.role_id;

  const rolesDisplay =
    normalizedUserRoles.length > 0
      ? normalizedUserRoles
          .map((x) => `${x.roleName}${x.defaultRole ? ' (Default)' : ''}`)
          .join(', ')
      : (r.role_name ?? '').trim();

  const designationId = r.designation_id ?? r.designationId;
  const { profilePictureIconUrl, profilePictureIconUrlFullpath } = extractProfilePictureFields(r);

  return {
    id: r.app_user_id,
    title: safeTitle,
    firstName: r.first_name ?? '',
    lastName: r.last_name ?? '',
    displayName: r.user_display_name ?? '',
    email: r.username ?? '',
    nicOrPassport: r.nic ?? '',
    role: roleName,
    roleId: typeof roleId === 'number' && roleId > 0 ? roleId : undefined,
    userRoles: normalizedUserRoles,
    rolesDisplay: rolesDisplay || undefined,
    division: ((r.division_name ?? '') as IntranetDivision) || '',
    divisionId: typeof r.division_id === 'number' ? r.division_id : undefined,
    designation: r.designation ?? '',
    designationId: typeof designationId === 'number' ? designationId : undefined,
    serviceId: r.service_id ?? '',
    contactNumber: r.contact_number ?? '',
    status: r.active ? 'Active' : 'Inactive',
    profilePictureIconFullpath: profilePictureIconUrlFullpath || undefined,
    profilePictureIconUrl: profilePictureIconUrl || undefined,
    createdAt: formatIntranetAuditDateTime(r.created_at),
    createdBy: r.created_by_name ?? '—',
    updatedAt: formatIntranetAuditDateTime(r.updated_at),
    updatedBy: r.updated_by_name ?? '—',
  };
}

function mapViewExternalToExtranetUser(r: ViewUserByIdResponse): ExtranetUser {
  const { profilePictureIconUrl, profilePictureIconUrlFullpath } = extractProfilePictureFields(r);

  return {
    id: r.app_user_id,
    agencyId: undefined,
    agencyName: r.agency_name ?? '',
    companyCode: r.company_code ?? '',
    brn: r.brn ?? '',
    displayName: r.user_display_name ?? '',
    email: r.username ?? '',
    contactPersonName: r.contact_person_name ?? '',
    contactPersonDesignation: r.contact_person_designation ?? '',
    contactNumber: r.contact_number ?? '',
    status: r.active ? 'Active' : 'Inactive',
    profilePictureIconFullpath: profilePictureIconUrlFullpath || undefined,
    profilePictureIconUrl: profilePictureIconUrl || undefined,
    createdAt: formatIntranetAuditDateTime(r.created_at),
    createdBy: r.created_by_name ?? '—',
    updatedAt: formatIntranetAuditDateTime(r.updated_at),
    updatedBy: r.updated_by_name ?? '—',
  };
}

export type ExtranetAgency = {
  id: number;
  name: string;
  createdAt: string;
  createdBy: string;
};

export type Permission = { id: string; label: string };

export type PermissionGroup = {
  id: string;
  label: string;
  permissions: Permission[];
};

export type Role = {
  id: string;
  name: string;
  code: string;
  description: string;
  status: AccountStatus;
  assignedUsers: number;
  permissions: string[]; // Permission IDs
  orderIndex: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

// (removed) permission groups mock data



export type InternalUserListItem = {
  id: string;
  displayName: string;
};

type InternalUserListItemResponse = {
  id?: string;
  displayname?: string;
  display_name?: string;
  displayName?: string;
  username?: string;
};

/**
 * CU-AUC-43 — GET `/v1/user/app-user/get-internal-user-list`.
 * Lightweight id + display name list for intranet user filter dropdowns.
 */
export async function listInternalUsers(input?: { applicationCode?: string }): Promise<ApiResult<InternalUserListItem[]>> {
  const applicationCode = (input?.applicationCode ?? import.meta.env.VITE_APP_CODE_INTERNAL_USER_LIST ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await apiRequest<InternalUserListItemResponse[]>(`/v1/user/app-user/get-internal-user-list`, {
    method: 'GET',
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };

  const mapped: InternalUserListItem[] = (res.data ?? [])
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const idRaw = o.id;
      const id = typeof idRaw === 'string' ? idRaw.trim() : '';
      const displayNameRaw = (o.displayname ?? o.display_name ?? o.displayName) as unknown;
      const displayName = typeof displayNameRaw === 'string' ? displayNameRaw.trim() : '';
      if (!id || !displayName) return null;
      return { id, displayName };
    })
    .filter((x): x is InternalUserListItem => Boolean(x))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { success: true, data: mapped };
}

/** All intranet user display names for filter dropdowns (CU-AUC-43). */
export async function listIntranetUserDisplayNames(): Promise<ApiResult<string[]>> {
  const res = await listInternalUsers();
  if (!res.success) return { success: false, message: res.message };
  const seen = new Set<string>();
  const names: string[] = [];
  for (const user of res.data) {
    const displayName = user.displayName.trim();
    if (!displayName) continue;
    const key = displayName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(displayName);
  }
  return { success: true, data: names };
}

export type IntranetOfficerOption = { displayName: string; division: string };

/** All intranet officers (name + division) for filter dropdowns (paginates through the list API). */
export async function listIntranetOfficerOptions(): Promise<ApiResult<IntranetOfficerOption[]>> {
  const seen = new Set<string>();
  const officers: IntranetOfficerOption[] = [];
  const pageSize = 100;
  let pageNumber = 0;
  let totalPages = 1;

  while (pageNumber < totalPages) {
    const res = await listIntranetUsers({ pageNumber, pageSize });
    if (!res.success) return { success: false, message: res.message };
    for (const u of res.data.items) {
      const displayName = u.displayName.trim();
      if (!displayName) continue;
      const division = (u.division ?? '').trim();
      const key = `${displayName.toLowerCase()}|${division.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      officers.push({ displayName, division });
    }
    if (res.data.items.length === 0) break;
    totalPages = res.data.totalPages;
    pageNumber += 1;
    if (pageNumber > 500) break;
  }

  officers.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return { success: true, data: officers };
}

export async function listIntranetUsers(args: {
  pageNumber: number;
  pageSize: number;
  name?: string;
  email?: string;
  role?: string;
  division?: string;
  active?: boolean | null;
}): Promise<ApiResult<IntranetUserListPage>> {
  const pageNumber = Math.max(0, args.pageNumber);
  const pageSize = Math.max(1, args.pageSize);
  const name = args.name?.replace(/\s+/g, ' ').trim() ?? '';
  const email = args.email?.replace(/\s+/g, ' ').trim() ?? '';
  const role = args.role?.replace(/\s+/g, ' ').trim() ?? '';
  const division = args.division?.replace(/\s+/g, ' ').trim() ?? '';

  const data: InternalUsersListRequest['data'] = {};
  if (name) data.name = name;
  if (email) data.email = email;
  if (role) data.role = role;
  if (division) data.division = division;
  if (args.active !== null && args.active !== undefined) data.active = args.active;

  const qs = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  const res = await apiRequest<InternalUsersListResponse>(`/v1/user/app-user/view-internal-users-list?${qs.toString()}`, {
    method: 'POST',
    body: { data } satisfies InternalUsersListRequest,
  });
  if (!res.ok) return { success: false, message: res.message };

  const body = res.data;
  const content = Array.isArray(body.content) ? body.content : [];
  const totalElements = typeof body.totalElements === 'number' ? body.totalElements : content.length;
  const computedPages = Math.max(1, Math.ceil(totalElements / pageSize) || 1);
  const totalPages =
    typeof body.totalPages === 'number' && body.totalPages > 0 ? body.totalPages : totalElements === 0 ? 1 : computedPages;
  const pageNum = typeof body.number === 'number' ? body.number : pageNumber;
  const sizeOut = typeof body.size === 'number' ? body.size : pageSize;

  const mapped: IntranetUser[] = content.map((u) => {
    const roleStr = (u.role ?? '').trim();
    const updatedRaw = listItemUpdatedAt(u);
    return {
      id: u.id,
      title: '',
      firstName: '',
      lastName: '',
      displayName: u.name,
      email: u.email,
      nicOrPassport: '',
      role: (roleStr.split(',')[0]?.replace(/\s*\(Default\)\s*$/i, '').trim() as IntranetUserRole) || 'Assigned Officer',
      userRoles: [],
      rolesDisplay: roleStr || undefined,
      division: (u.division as IntranetDivision) ?? '',
      designation: '',
      serviceId: '',
      contactNumber: '',
      status: u.active ? 'Active' : 'Inactive',
      createdAt: u.createdAt ?? u.created_at ? formatIntranetAuditDateTime(u.createdAt ?? u.created_at) : '—',
      createdBy: '—',
      updatedAt: updatedRaw ? formatIntranetAuditDateTime(updatedRaw) : '—',
      updatedBy: '—',
    };
  });

  return {
    success: true,
    data: {
      items: mapped,
      totalElements,
      totalPages,
      pageNumber: pageNum,
      pageSize: sizeOut,
    },
  };
}

export type ActiveRoleUserRow = {
  roleId: number;
  roleName: string;
  moduleId?: number;
  orderIndex: number;
  availableOnExtranet: boolean;
};

/** CU-MRC-15 — GET `/v1/user/msth-role/active-users`.
 * Returns only roles where `active = true`.
 */
export async function listActiveUserRoles(input?: { applicationCode?: string }): Promise<ApiResult<ActiveRoleUserRow[]>> {
  const applicationCode = (input?.applicationCode ?? import.meta.env.VITE_APP_CODE_ACTIVE_USERS_ROLES ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await apiRequest<Array<{ role_id: number; role_name: string; module_id?: number; order_index?: number; available_on_extranet?: boolean }>>(
    `/v1/user/msth-role/active-users`,
    {
      method: 'GET',
      headers: { applicationCode, 'application-code': applicationCode },
    }
  );

  if (!res.ok) return { success: false, message: res.message };

  const mapped: ActiveRoleUserRow[] = (res.data ?? [])
    .map((r) => ({
      roleId: r.role_id,
      roleName: (r.role_name ?? '').trim(),
      moduleId: r.module_id,
      orderIndex: (r.order_index ?? 0) as number,
      availableOnExtranet: Boolean(r.available_on_extranet),
    }))
    .filter((r) => r.roleName.length > 0)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  return { success: true, data: mapped };
}

export async function getIntranetUser(id: string): Promise<ApiResult<IntranetUser>> {
  const res = await apiRequest<ViewUserByIdResponse>(`/v1/user/app-user/view-user-by-id?appUserId=${encodeURIComponent(id)}`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: mapViewInternalToIntranetUser(res.data) };
}


export async function createIntranetUser(user: IntranetUser): Promise<ApiResult<IntranetUser>> {
  const payload = intranetToCreatePayload(user);
  const res = await apiRequest<unknown>('/v1/user/app-user', { method: 'POST', body: payload });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: user };
}

export async function updateIntranetUser(id: string, user: IntranetUser): Promise<ApiResult<IntranetUser>> {
  const payload = intranetToCreatePayload(user);
  const res = await apiRequest<unknown>(`/v1/user/app-user/id/${encodeURIComponent(id)}`, { method: 'PUT', body: payload });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: { ...user, id } };
}

export async function listExtranetUsers(args?: {
  agencyName?: string;
  email?: string;
  status?: '' | AccountStatus;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ApiResult<ExtranetUser[]>> {
  const agencyName = args?.agencyName?.trim() ?? '';
  const email = args?.email?.trim() ?? '';
  const status = args?.status ?? '';

  const active = !status ? undefined : status === 'Active';

  const pageNumber = args?.pageNumber ?? 0;
  // Keep client-side pagination for now; fetch all.
  const pageSize = args?.pageSize ?? 2147483647;

  const reqBody: ExternalUsersListRequest = {
    data: {
      agency_name: agencyName || undefined,
      email: email || undefined,
      active,
    },
  };

  const res = await apiRequest<ExternalUsersListResponse>(
    `/v1/user/app-user/view-external-users-list?pageNumber=${encodeURIComponent(pageNumber)}&pageSize=${encodeURIComponent(pageSize)}`,
    {
      method: 'POST',
      body: reqBody,
      headers: withAppCode(import.meta.env.VITE_APP_CODE_EXTERNAL_USERS_LIST ?? ''),
    }
  );
  if (!res.ok) return { success: false, message: res.message };

  const mapped: ExtranetUser[] = res.data.content.map((u) => {
    return {
      id: u.user_id,
      agencyName: u.agency_name,
      companyCode: u.code,
      brn: '',
      displayName: u.agency_name,
      email: u.email,
      contactPersonName: u.contact_person,
      contactPersonDesignation: '',
      contactNumber: '',
      status: u.active ? 'Active' : 'Inactive',
      createdAt: formatIntranetAuditDateTime(u.date_added),
      createdBy: '—',
      updatedAt: formatIntranetAuditDateTime(u.last_updated_at),
      updatedBy: '—',
    };
  });

  return { success: true, data: mapped };
}

export async function listExtranetAgencies(): Promise<ApiResult<ExtranetAgency[]>> {
  const res = await apiRequest<{ status?: string; response_result?: unknown; message?: string }>(`/v1/user/mst-agency/list`, {
    method: 'GET',
    headers: withAppCode(import.meta.env.VITE_APP_CODE_AGENCY_LIST ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };

  const raw = res.data?.response_result;
  const arr = Array.isArray(raw) ? raw : [];
  const mapped: ExtranetAgency[] = arr
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const idRaw = (o.agency_id ?? o.agencyId ?? o.id) as unknown;
      const nameRaw = (o.agency_name ?? o.agencyName ?? o.name) as unknown;
      const id = typeof idRaw === 'number' ? idRaw : typeof idRaw === 'string' && idRaw.trim() ? Number(idRaw) : NaN;
      const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
      if (!Number.isFinite(id) || !name) return null;
      return { id, name, createdAt: '—', createdBy: '—' } satisfies ExtranetAgency;
    })
    .filter((x): x is ExtranetAgency => Boolean(x))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { success: true, data: mapped };
}

export async function listDivisions(applicationCode = import.meta.env.VITE_APP_CODE_DIVISIONS ?? ''): Promise<ApiResult<DivisionListItem[]>> {
  const res = await apiRequest<DivisionListItem[]>('/v1/user/app-user/divisions', {
    method: 'GET',
    headers: {
      // Spec uses applicationCode; backend may also accept application-code.
      applicationCode,
      'application-code': applicationCode,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

export async function getDesignationList(): Promise<ApiResult<DesignationListItem[]>> {
  const res = await apiRequest<DesignationListItem[]>('/v1/user/designation-list', {
    method: 'GET',
    headers: withAppCode('ww'),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = Array.isArray(res.data) ? res.data : [];
  return { success: true, data };
}

function isAgencyApiSuccess(status: unknown): boolean {
  if (typeof status !== 'string') return true; // some BE responses may omit it
  const s = status.trim().toLowerCase();
  return s === 'success' || s === 'ok' || s === 'true';
}

export async function validateExtranetAgencyName(agencyName: string): Promise<ApiResult<void>> {
  const cleaned = agencyName.replace(/\s+/g, ' ').trim();
  if (!cleaned) return { success: false, message: 'Extranet agency name is required.' };

  const qs = new URLSearchParams({ agencyName: cleaned });
  const res = await apiRequest<{ status?: string; response_result?: unknown; message?: string }>(`/v1/user/mst-agency/validate-agency-name?${qs.toString()}`, {
    method: 'GET',
    headers: withAppCode(import.meta.env.VITE_APP_CODE_AGENCY_VALIDATE_NAME ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };

  // Some backends return 200 with status/message indicating invalid/existing.
  if (!isAgencyApiSuccess(res.data?.status)) return { success: false, message: res.data?.message?.trim() || 'Validation failed.' };

  // If response_result is a boolean-ish "exists" flag, treat it as such.
  const rr = res.data?.response_result;
  if (typeof rr === 'boolean' && rr === true) return { success: false, message: res.data?.message?.trim() || 'This external agency already exists.' };
  if (typeof rr === 'string') {
    const v = rr.trim().toLowerCase();
    if (v === 'true' || v === 'exists' || v === 'already_exists') return { success: false, message: res.data?.message?.trim() || 'This external agency already exists.' };
  }

  return { success: true, data: undefined };
}

export async function createExtranetAgency(name: string): Promise<ApiResult<ExtranetAgency>> {
  const cleaned = name.replace(/\s+/g, ' ').trim();
  if (!cleaned) return { success: false, message: 'Extranet agency name is required.' };

  const res = await apiRequest<{ status?: string; response_result?: unknown; message?: string }>(`/v1/user/mst-agency/create-agency`, {
    method: 'POST',
    body: { data: { name: cleaned } },
    headers: withAppCode(import.meta.env.VITE_APP_CODE_AGENCY_CREATE ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isAgencyApiSuccess(res.data?.status)) return { success: false, message: res.data?.message?.trim() || 'Failed to create external agency.' };

  const rr = res.data?.response_result;
  if (rr && typeof rr === 'object') {
    const o = rr as Record<string, unknown>;
    const idRaw = (o.agency_id ?? o.agencyId ?? o.id) as unknown;
    const nameRaw = (o.agency_name ?? o.agencyName ?? o.name) as unknown;
    const id = typeof idRaw === 'number' ? idRaw : typeof idRaw === 'string' && idRaw.trim() ? Number(idRaw) : NaN;
    const nm = typeof nameRaw === 'string' && nameRaw.trim() ? nameRaw.trim() : cleaned;
    if (Number.isFinite(id)) return { success: true, data: { id, name: nm, createdAt: '—', createdBy: '—' } };
  }

  // Fallback: refresh list after create and match by name
  const list = await listExtranetAgencies();
  if (list.success) {
    const match = list.data.find((a) => a.name.toLowerCase() === cleaned.toLowerCase());
    if (match) return { success: true, data: match };
  }

  return { success: true, data: { id: 0, name: cleaned, createdAt: '—', createdBy: '—' } };
}

export async function getExtranetUser(id: string): Promise<ApiResult<ExtranetUser>> {
  const res = await apiRequest<ViewUserByIdResponse>(`/v1/user/app-user/view-external-user-by-id?appUserId=${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: withAppCode(import.meta.env.VITE_APP_CODE_EXTERNAL_USER_BY_ID ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: mapViewExternalToExtranetUser(res.data) };
}

export async function createExtranetUser(user: ExtranetUser): Promise<ApiResult<ExtranetUser>> {
  const payload = extranetToCreatePayload(user);
  const res = await apiRequest<unknown>('/v1/user/app-user/external', {
    method: 'POST',
    body: payload,
    headers: withAppCode(import.meta.env.VITE_APP_CODE_EXTERNAL_USER_CREATE ?? ''),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: user };
}

export async function updateExtranetUser(id: string, user: ExtranetUser): Promise<ApiResult<ExtranetUser>> {
  const payload = extranetToCreatePayload(user);

  // Spec shows `/v1/user/app-userupdate-external/id/{id}` (missing slash). Try the sane path first, then fallback.
  const primaryPath = `/v1/user/app-user/update-external/id/${encodeURIComponent(id)}`;
  const fallbackPath = `/v1/user/app-userupdate-external/id/${encodeURIComponent(id)}`;

  const headers = withAppCode(import.meta.env.VITE_APP_CODE_EXTERNAL_USER_UPDATE ?? '');

  const primary = await apiRequest<unknown>(primaryPath, { method: 'PUT', body: payload, headers });
  if (primary.ok) return { success: true, data: { ...user, id } };

  const fallback = await apiRequest<unknown>(fallbackPath, { method: 'PUT', body: payload, headers });
  if (!fallback.ok) return { success: false, message: fallback.message };
  return { success: true, data: { ...user, id } };
}

export async function deleteAppUser(id: string): Promise<ApiResult<void>> {
  const cleaned = id.trim();
  if (!cleaned) return { success: false, message: 'Missing user id.' };

  if (import.meta.env.DEV) console.log('[deleteAppUser] calling', { id: cleaned });

  const res = await apiRequest<{ status?: string; response_result?: unknown; message?: string }>(
    `/v1/user/app-user/delete/${encodeURIComponent(cleaned)}`,
    {
      method: 'DELETE',
      headers: withAppCode(import.meta.env.VITE_APP_CODE_USER_DELETE ?? ''),
    }
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}